import { SessionContext } from "@/context/SessionContext";
import ConductionAPI, { ClassroomForm } from "@/javascript/conduction-api";
import { Song } from "@/javascript/library-objects";
import { Instrument } from "@/javascript/music-objects";
import { Utils } from "@/javascript/utils";
import {
	Button,
	Group,
	Modal,
	MultiSelect,
	NumberInput,
	Skeleton,
	Stack,
	Text,
	TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useContext, useEffect, useMemo, useState } from "react";

interface AdminClassroomCreatorProps {
	showModal: boolean;
	setShowModal: (show: boolean) => void;
	onCreate?: () => void; // Optional callback to refresh the classroom list after creation
}

type SelectOption = { label: string; value: string };

const ClassroomCreatorModal: React.FC<AdminClassroomCreatorProps> = ({
	showModal,
	setShowModal,
	onCreate
}) => {
	const [instruments, setInstruments] = useState<Instrument[]>([]);
	const [loadingInstruments, setLoadingInstruments] = useState<boolean>(true);
	const [songs, setSongs] = useState<Song[]>([]);
	const [loadingSongs, setLoadingSongs] = useState<boolean>(true);
	const [submitting, setSubmitting] = useState<boolean>(false);

	const session = useContext(SessionContext);
	if (!session) throw new Error("AdminClassroomCreator must be a child of SessionContext.");
	const token = session.token;
	if (token === null) return null;

	const form = useForm({
		initialValues: {
			name: "",
			studentLimit: 0,
			classroomAdminLimit: 0,
			// IMPORTANT: MultiSelect value is string[] of selected "value" ids
			defaultInstruments: [] as string[],
			defaultSongs: [] as string[],
		},
		validate: {
			name: (value) => (value.trim().length === 0 ? "Classroom name is required" : null),
			studentLimit: (value) => (value < 0 ? "Student limit must not be negative" : null),
			classroomAdminLimit: (value) =>
				value < 0 ? "Classroom admin must not be negative" : null,
		},
	});

	useEffect(() => {
		const fetchInstruments = async () => {
			try {
				const list = await ConductionAPI.getInstruments();
				setInstruments(list || []);
			} catch (error) {
				console.error("Error fetching instrument options", error);
			} finally {
				setLoadingInstruments(false);
			}
		};

		const fetchSongs = async () => {
			try {
				const list = await ConductionAPI.getAllSongs(token);
				setSongs(list || []);
			} catch (error) {
				console.error("Error fetching song options", error);
			} finally {
				setLoadingSongs(false);
			}
		};

		fetchInstruments();
		fetchSongs();
	}, [token]);

	const instrumentOptions: SelectOption[] = useMemo(
		() =>
			instruments.map((instrument) => ({
				label: instrument.name,
				value: instrument.id,
			})),
		[instruments]
	);

	const songOptions: SelectOption[] = useMemo(
		() =>
			songs.map((song) => ({
				label: `${song.songName} by ${(song.artists ?? [])
					.map((a) => a.artistName)
					.join(", ")}`,
				value: song.songId,
			})),
		[songs]
	);

	const handleSubmit = async () => {
		const { hasErrors } = form.validate();
		if (hasErrors) return;

		const notificationId = Utils.displayNotification(
			"Creating Classroom...",
			"Your new classroom is being created.",
			"blue",
			"loading"
		);

		const classroomForm: ClassroomForm = {
			name: form.values.name,
			studentLimit: form.values.studentLimit,
			classroomAdminLimit: form.values.classroomAdminLimit,
			// Values are already string[] ids
			defaultInstrumentIds: form.values.defaultInstruments,
			defaultSongIds: form.values.defaultSongs,
		};

		try {
			setSubmitting(true);
			await ConductionAPI.createClassroom(classroomForm, token);
			Utils.updateNotification(
				notificationId,
				"Classroom Created Successfully",
				undefined,
				"green",
				<IconCheck size={16} />
			);
			if (onCreate) {
				onCreate();
			}
			setShowModal(false);
			form.reset();
		} catch (error) {
			console.error("Error creating classroom", error);
			Utils.updateNotification(
				notificationId,
				"Error",
				"Failed to create classroom.",
				"red",
				<IconAlertCircle size={16} />,
				true
			);
		} finally {
			setSubmitting(false);
		}
	};

	const addAllInstruments = () =>
		form.setFieldValue(
			"defaultInstruments",
			instrumentOptions.map((o) => o.value)
		);

	const addAllSongs = () =>
		form.setFieldValue(
			"defaultSongs",
			songOptions.map((o) => o.value)
		);

	return (
		<Modal opened={showModal} onClose={() => setShowModal(false)} title="Create New Classroom" size="xl">
			{loadingInstruments || loadingSongs ? (
				<Skeleton height={"100%"} radius="md" />
			) : (
				<form onSubmit={form.onSubmit(handleSubmit)}>
					<Stack>
						<TextInput label="Classroom Name" {...form.getInputProps("name")} required />

						<NumberInput label="Student Limit" {...form.getInputProps("studentLimit")} required />

						<NumberInput
							label="Classroom Admin Limit"
							{...form.getInputProps("classroomAdminLimit")}
							required
						/>

						<Stack gap={0}>
							<Text size="sm">Instruments</Text>
							<Group wrap="nowrap" align="flex-start">
								<MultiSelect
									data={instrumentOptions}
									{...form.getInputProps("defaultInstruments")}
									searchable
									clearable
									style={{ flex: 1 }}
								/>
								{/* Add all */}
								<Button
									type="button"
									size="compact-xs"
									variant="subtle"
									onClick={addAllInstruments}
									disabled={
										instrumentOptions.length === 0 ||
										form.values.defaultInstruments.length === instrumentOptions.length
									}
									leftSection={<IconCheck size={16} />}
								>
									Add All
								</Button>
							</Group>
						</Stack>

						<Stack gap={0}>
							<Text size="sm">Songs</Text>
							<Group wrap="nowrap" align="flex-start">
								<MultiSelect
									data={songOptions}
									{...form.getInputProps("defaultSongs")}
									searchable
									clearable
									style={{ flex: 1 }}
								/>
								{/* Add all */}
								<Button
									type="button"
									size="compact-xs"
									variant="subtle"
									onClick={addAllSongs}
									disabled={
										songOptions.length === 0 ||
										form.values.defaultSongs.length === songOptions.length
									}
									leftSection={<IconCheck size={16} />}
								>
									Add All
								</Button>
							</Group>
						</Stack>

						<Button type="submit" radius="xl" size="sm" loading={submitting}>
							Create Classroom
						</Button>
					</Stack>
				</form>
			)}
		</Modal>
	);
};

export default ClassroomCreatorModal;
